import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SlidePanel } from "@/components/ui/slide-panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ClipboardList } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { describeSupabaseError } from "@/lib/supabaseErrorMessage";
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { useToast } from "@/components/ui/use-toast";
import { ChecklistItemManager } from './ChecklistItemManager';
import { SearchableSelect } from "@/components/ui/searchable-select";
import { FormField } from "@/components/ui/form-field";
import { ChecklistCard } from './ChecklistCard';
import { TemplateChecklistItemManager } from './TemplateChecklistItemManager';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from '@hello-pangea/dnd';
import { libraryItemLink } from '@/lib/libraryItemLink';

// Define types based on database schema
interface ChecklistTemplate {
  id: number | string;
  stage_id?: number;
  stage_name?: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  checklist_type?: string;
  display_order?: number;
  pipeline_id?: number; // New field for pipeline-specific checklists
  displayName?: string; // For template loading display
  pipelineName?: string; // For template loading display
  pipelines?: {
    id: number;
    name: string;
    pipeline_type?: string;
  };
}

// Define types for templates library
interface TemplateLibraryItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  stage_name?: string;
  pipeline_id?: number;
  pipeline_name?: string;
  scope: 'global' | 'tenant' | 'shared';
  configuration: any;
  total_items_count?: number;
  created_at: string;
  updated_at: string;
  displayName?: string; // For template loading display
  categoryName?: string; // For template loading display
}

interface Stage {
  id: number;
  name: string;
  stage_order: number;
  pipeline_id: number;
}

interface ChecklistsTabProps {
  onRefreshData?: () => void;
  pipelineId?: number; // Optional: if provided, filter to this specific pipeline
  pipelineType?: 'commercial' | 'residential' | 'onboarding' | 'internal'; // Optional: if provided, set the active tab
  templateMode?: boolean; // NEW: if true, use checklist_templates_library instead of checklist_templates
  templateId?: string; // NEW: if provided in template mode, filter to this specific template
  /**
   * Passed straight through to ChecklistItemManager, which renders here with
   * `noPanel` and therefore has no close control of its own. See that
   * component's prop for why the guard has to reach the panel owner.
   */
  registerExitGuard?: (guard: ((exit: () => void) => void) | null) => void;
}

export const ChecklistsTab = ({ onRefreshData, pipelineId, pipelineType, templateMode = false, templateId, registerExitGuard }: ChecklistsTabProps) => {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  
  // State for templates and stages
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);

  // Create stage options for dropdowns
  const stageOptions = stages.map((stage, idx) => ({
    value: stage.id ? stage.id.toString() : `stage-${idx}`,
    label: stage.name
  }));
  const [checklistItems, setChecklistItems] = useState<Record<string, any[]>>({});
  const [pipelines, setPipelines] = useState<Array<{ id: number; name: string; pipeline_type?: string }>>([]);
  
  // Drag and drop state
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Dialog states
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ChecklistTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<ChecklistTemplate | null>(null);
  const [showItemManager, setShowItemManager] = useState(false);
  const [showInlineTemplateForm, setShowInlineTemplateForm] = useState(false);
  const [showInlineDeleteConfirmation, setShowInlineDeleteConfirmation] = useState(false);
  
  // Template loading states
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateSelectionMode, setTemplateSelectionMode] = useState<'create' | 'load'>('create');
  const [availableTemplates, setAvailableTemplates] = useState<TemplateLibraryItem[]>([]);
  const [selectedTemplateToLoad, setSelectedTemplateToLoad] = useState<string>('');
  // EXP-1.3 · point back. The staged items in checklistItems['new_template']
  // come from a library template, and the apply insert below has to record
  // WHICH one on every item it creates. `selectedTemplateToLoad` is cleared as
  // soon as the template is loaded, so the id is kept here instead.
  const [loadedLibraryTemplateId, setLoadedLibraryTemplateId] = useState<string | null>(null);
  
  // Form state
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    stage_id: "",
    checklist_type: "residential",
    pipeline_id: "" // New field for pipeline selection
  });

  // Toggle state for residential/commercial checklists
  // In template mode, use the pipelineType to determine which tab to show
  // For 'internal' and 'onboarding' types, default to 'residential'
  const [activeChecklistTab, setActiveChecklistTab] = useState(
    pipelineType === 'commercial' ? 'commercial' :
    pipelineType === 'residential' ? 'residential' :
    "residential"
  );

  // Sync activeChecklistTab with pipelineType when it changes (for template mode)
  useEffect(() => {
    if (templateMode && pipelineType) {
      const newTab = pipelineType === 'commercial' ? 'commercial' : 'residential';
      setActiveChecklistTab(newTab);
    }
  }, [pipelineType, templateMode]);

  // Load data on component mount and when tenant changes
  useEffect(() => {
    fetchTemplates();
  }, [pipelineId, pipelineType, currentTenant?.tenant_id]);

  // Pre-select pipeline and checklist type when pipelineId is provided
  useEffect(() => {
    if (pipelineId && pipelineType) {
      setNewTemplate(prev => {
        const updated = {
          ...prev,
          checklist_type: pipelineType,
          pipeline_id: pipelineId.toString()
        };
        return updated;
      });
    }
  }, [pipelineId, pipelineType]);

  // Handle drag end for reordering templates
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const items = Array.from(templates);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setTemplates(items);
    setHasOrderChanges(true);
  };

  // Save template order changes
  const saveTemplateOrder = async () => {
    try {
      setSavingOrder(true);
      
      // Update display_order for all templates
      for (let i = 0; i < templates.length; i++) {
        const template = templates[i];
        const { error } = await supabase
          .from('checklist_templates')
          .update({ display_order: i })
          .eq('id', template.id);
        
        if (error) {
          console.error('Error updating template order:', error);
          throw error;
        }
      }
      
      setHasOrderChanges(false);
      toast({
        title: "Success",
        description: "Checklist order saved",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating template order:', error);
      toast({
        title: "Error",
        description: "Failed to save checklist order",
        variant: "destructive",
      });
      // Refresh templates to revert changes
      fetchTemplates();
    } finally {
      setSavingOrder(false);
    }
  };
  
  // Function to move a template up in the list
  const handleMoveUp = async (template: ChecklistTemplate, index: number, type: 'residential' | 'commercial') => {
    // Can't move the first item up
    if (index === 0) return;
    
    // Get the filtered templates of the same type
    const filteredTemplates = templates.filter(t => 
      type === 'residential' ? t.checklist_type !== 'commercial' : t.checklist_type === 'commercial'
    );
    
    // Get the template to swap with
    const prevTemplate = filteredTemplates[index - 1];
    
    try {
      setLoading(true);
      
      // Update the display order in the database
      const { error: error1 } = await supabase
        .from('checklist_templates')
        .update({ display_order: index - 1 })
        .eq('id', template.id);
        
      const { error: error2 } = await supabase
        .from('checklist_templates')
        .update({ display_order: index })
        .eq('id', prevTemplate.id);
      
      if (error1 || error2) {
        throw error1 || error2;
      }
      
      // Update local state
      const updatedTemplates = [...templates];
      const templateIndex = updatedTemplates.findIndex(t => t.id === template.id);
      const prevTemplateIndex = updatedTemplates.findIndex(t => t.id === prevTemplate.id);
      
      // Swap display_order values
      const tempOrder = updatedTemplates[templateIndex].display_order;
      updatedTemplates[templateIndex].display_order = updatedTemplates[prevTemplateIndex].display_order;
      updatedTemplates[prevTemplateIndex].display_order = tempOrder;
      
      // Sort by display_order
      updatedTemplates.sort((a, b) => {
        // First sort by checklist_type to keep residential and commercial separate
        if (a.checklist_type !== b.checklist_type) {
          return a.checklist_type === 'commercial' ? 1 : -1;
        }
        // Then sort by display_order
        return (a.display_order || 0) - (b.display_order || 0);
      });
      
      setTemplates(updatedTemplates);
      
      toast({
        title: "Success",
        description: "Checklist order updated",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating checklist order:', error);
      toast({
        title: "Error",
        description: "Failed to update checklist order",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Function to move a template down in the list
  const handleMoveDown = async (template: ChecklistTemplate, index: number, type: 'residential' | 'commercial') => {
    // Get the filtered templates of the same type
    const filteredTemplates = templates.filter(t => 
      type === 'residential' ? t.checklist_type !== 'commercial' : t.checklist_type === 'commercial'
    );
    
    // Can't move the last item down
    if (index === filteredTemplates.length - 1) return;
    
    // Get the template to swap with
    const nextTemplate = filteredTemplates[index + 1];
    
    try {
      setLoading(true);
      
      // Update the display order in the database
      const { error: error1 } = await supabase
        .from('checklist_templates')
        .update({ display_order: index + 1 })
        .eq('id', template.id);
        
      const { error: error2 } = await supabase
        .from('checklist_templates')
        .update({ display_order: index })
        .eq('id', nextTemplate.id);
      
      if (error1 || error2) {
        throw error1 || error2;
      }
      
      // Update local state
      const updatedTemplates = [...templates];
      const templateIndex = updatedTemplates.findIndex(t => t.id === template.id);
      const nextTemplateIndex = updatedTemplates.findIndex(t => t.id === nextTemplate.id);
      
      // Swap display_order values
      const tempOrder = updatedTemplates[templateIndex].display_order;
      updatedTemplates[templateIndex].display_order = updatedTemplates[nextTemplateIndex].display_order;
      updatedTemplates[nextTemplateIndex].display_order = tempOrder;
      
      // Sort by display_order
      updatedTemplates.sort((a, b) => {
        // First sort by checklist_type to keep residential and commercial separate
        if (a.checklist_type !== b.checklist_type) {
          return a.checklist_type === 'commercial' ? 1 : -1;
        }
        // Then sort by display_order
        return (a.display_order || 0) - (b.display_order || 0);
      });
      
      setTemplates(updatedTemplates);
      
      toast({
        title: "Success",
        description: "Checklist order updated",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating checklist order:', error);
      toast({
        title: "Error",
        description: "Failed to update checklist order",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch checklist templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        throw new Error('Failed to get tenant ID');
      }

      // First get the pipeline ID for the commercial pipeline
      const { data: pipelinesData, error: pipelinesError } = await supabase
        .from('pipelines')
        .select('*')
        .eq('tenant_id', tenantId);
      
      if (pipelinesError) {
        console.error('Error fetching pipelines:', pipelinesError);
        throw pipelinesError;
      }
      
      // Store pipelines in state
      setPipelines(pipelinesData || []);

      // CLN-6.3: removed a lookup of 'Solerro Commercial Pipeline' /
      // 'Solerro Residential Pipeline' that assigned `pipelineIds.current` with
      // `|| 1` and `|| 2` as fallbacks. Those are literal pipeline ids belonging to
      // whichever tenant owns them, so for every other tenant the ref held a
      // foreign pipeline id. It was never read: the ref was declared, written in
      // these two places, and read nowhere in the file. Dead, and dangerous only if
      // someone had started using it.

      // Fetch stages based on mode
      let stagesData;
      const stageOrderMap = new Map();

      if (templateMode && templateId) {
        // In template mode, fetch stages from the pipeline template's JSONB field
        const { data: templateData, error: templateError } = await supabase
          .from('pipeline_templates_library')
          .select('stages')
          .eq('id', templateId)
          .single();

        if (templateError) {
          console.error('Error fetching pipeline template:', templateError);
          throw templateError;
        }

        // Extract stages from JSONB and map them to the expected format
        stagesData = templateData?.stages || [];

        // Create stage order map using the stage_order from template stages
        stagesData.forEach((stage: any) => {
          stageOrderMap.set(stage.name, stage.stage_order);
        });
      } else {
        // In pipeline mode, fetch stages from the stages table
        // If pipelineId prop is provided, only fetch stages for that specific pipeline
        // Otherwise fetch stages for all tenant pipelines
        const pipelineIdList = pipelineId ? [pipelineId] : pipelinesData.map(p => p.id);
        const { data: liveStagesData, error: stagesError } = await supabase
          .from('stages')
          .select('id, name, stage_order, pipeline_id')
          .in('pipeline_id', pipelineIdList)
          .order('stage_order', { ascending: true });

        if (stagesError) {
          console.error('Error fetching stages:', stagesError);
          throw stagesError;
        }

        stagesData = liveStagesData || [];

        // Create a map of stage IDs to their order for sorting
        liveStagesData?.forEach(stage => {
          stageOrderMap.set(stage.id, stage.stage_order);
        });
      }

      // Store all stages for reference
      setStages(stagesData || []);

      // Get stage IDs for tenant filtering
      const stageIdList = stagesData?.map((s: any) => s.id) || [];
      
      // Fetch templates - use library table in template mode, regular table in pipeline mode
      let templatesData, templatesError;

      if (templateMode) {
        // Template mode: use checklist_templates_library, filter by pipeline template if provided
        // Note: RLS policies handle tenant filtering - global templates are visible to all tenants
        let query = supabase
          .from('checklist_templates_library')
          .select(`
            id,
            name,
            description,
            category,
            stage_name,
            pipeline_id,
            pipeline_name,
            pipeline_template_id,
            scope,
            configuration,
            is_legacy,
            created_at,
            updated_at
          `)
          .eq('is_active', true);

        // If templateId is provided, filter to checklist templates linked to this pipeline template
        if (templateId) {
          query = query.eq('pipeline_template_id', templateId);
        }

        const { data, error } = await query.order('created_at', { ascending: true });

        // Calculate total_items_count from configuration JSONB
        if (data) {
          templatesData = data.map((template: any) => ({
            ...template,
            total_items_count: template.configuration?.items?.length || 0
          }));
        } else {
          templatesData = data;
        }

        templatesError = error;
      } else {
        // Pipeline mode: use checklist_templates (existing logic)
        let query = supabase
          .from('checklist_templates')
          .select(`
            *,
            stages:stage_id(id, name, stage_order)
          `)
          .in('stage_id', stageIdList); // Filter by tenant's stages

        // If pipelineId is provided, filter by that specific pipeline
        if (pipelineId) {
          query = query.eq('pipeline_id', pipelineId);
        }

        // Order by display_order to maintain correct checklist order
        query = query.order('display_order', { ascending: true });

        const { data, error } = await query;
        templatesData = data;
        templatesError = error;
      }

      if (templatesError) {
        // If the table doesn't exist, use an empty array
        if (templatesError.code === '42P01') { // PostgreSQL code for "relation does not exist"
          setTemplates([]);
          setLoading(false);
          return;
        }
        throw templatesError;
      }

      // Set default checklist_type to 'residential' for existing templates and assign display_order if missing
      const templatesWithType = templatesData?.map((template, index) => ({
        ...template,
        checklist_type: template.checklist_type || 'residential',
        // In template mode, get display_order from configuration JSONB, otherwise use the field directly
        display_order: templateMode
          ? (template.configuration?.display_order ?? index)
          : (template.display_order !== null && template.display_order !== undefined ? template.display_order : index)
      })) || [];

      // Templates are now prepared with type and display order information

      // Sort templates by display_order first, then by stage_order as a fallback
      const sortedTemplates = [...templatesWithType].sort((a, b) => {
        // First check if they're the same type (residential or commercial)
        const sameType = a.checklist_type === b.checklist_type;

        if (sameType) {
          // If they're the same type, sort by display_order
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
        }

        // If they're different types or display_order is not available, fall back to stage order
        let stageOrderA, stageOrderB;

        if (templateMode) {
          // In template mode, use stage_name to look up order
          stageOrderA = stageOrderMap.get(a.stage_name) || 9999;
          stageOrderB = stageOrderMap.get(b.stage_name) || 9999;
        } else {
          // In pipeline mode, use stage_id to look up order
          stageOrderA = a.stages?.stage_order || stageOrderMap.get(a.stage_id) || 9999;
          stageOrderB = b.stages?.stage_order || stageOrderMap.get(b.stage_id) || 9999;
        }

        return stageOrderA - stageOrderB;
      });
        
      // Templates are now sorted by display_order and ready to be used

      setTemplates(sortedTemplates);

      // Fetch items for each template
      if (templatesData) {
        for (const template of templatesData) {
          fetchTemplateItems(template.id, template);
        }
      }
    } catch (error) {
      console.error('Error fetching checklist templates:', error);
      toast({
        title: "Error",
        description: "Failed to load checklist templates.",
        variant: "destructive",
      });
      // Set templates to empty array on error
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch items for a specific template
  const fetchTemplateItems = async (templateId: number | string, template?: any) => {
    try {
      if (templateMode && template?.configuration) {
        // In template mode, items are stored in JSONB configuration
        const items = template.configuration.items || [];
        setChecklistItems(prev => ({
          ...prev,
          [templateId]: items
        }));
      } else {
        // In pipeline mode, fetch from checklist_items table
        const { data, error } = await supabase
          .from('checklist_items')
          .select('*')
          .eq('template_id', templateId)
          .order('item_order');

        if (error) throw error;

        setChecklistItems(prev => ({
          ...prev,
          [templateId]: data || []
        }));
      }
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    }
  };

  // Handle input change for template form
  const handleTemplateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewTemplate({
      ...newTemplate,
      [e.target.name]: e.target.value
    });
  };

  // Handle select change for template form
  const handleTemplateSelectChange = (name: string, value: string) => {
    setNewTemplate(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // If stage is selected, default the name to the stage name
      if (name === 'stage_id' && value) {
        const selectedStage = stages.find(stage => stage.id.toString() === value);
        if (selectedStage && !prev.name) {
          updated.name = selectedStage.name;
        }
      }
      
      return updated;
    });
  };

  // Fetch available templates for loading
  const fetchAvailableTemplates = async () => {
    try {
      setIsLoadingTemplate(true);
      
      // Get all templates from the templates library
      const { data: templatesData, error } = await supabase
        .from('checklist_templates_library')
        .select(`
          id,
          name,
          description,
          category,
          stage_name,
          pipeline_id,
          pipeline_name,
          scope,
          configuration,
          total_items_count,
          created_at,
          updated_at
        `)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;

      // Group templates by category for better organization
      const groupedTemplates = templatesData?.map(template => ({
        ...template,
        displayName: template.name,
        categoryName: template.category || 'Uncategorized'
      })) || [];

      setAvailableTemplates(groupedTemplates);
    } catch (error) {
      console.error('Error fetching available templates:', error);
      toast({
        title: "Error",
        description: "Failed to load available templates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Load template into the form
  const handleLoadTemplate = async () => {
    if (!selectedTemplateToLoad) return;

    const template = availableTemplates.find(t => t.id === selectedTemplateToLoad);
    if (!template) return;

    try {
      setIsLoadingTemplate(true);

      // Extract items from the template configuration
      const configuration = template.configuration || {};
      const items = configuration.items || [];

      // Update the form with template data
      setNewTemplate({
        name: template.name,
        description: template.description || '',
        stage_id: '', // Will need to be selected by user
        checklist_type: 'residential', // Default, can be changed
        pipeline_id: template.pipeline_id?.toString() || ''
      });

      // Store the items to be added when the template is created
      setChecklistItems(prev => ({
        ...prev,
        'new_template': items
      }));

      // EXP-1.3: keep the library template id alongside them. Without it the
      // applied items carry no link back and their example chip stays empty.
      setLoadedLibraryTemplateId(String(template.id));

      // Switch back to create mode but keep the loaded data
      setTemplateSelectionMode('create');
      setSelectedTemplateToLoad('');

      toast({
        title: "Template Loaded",
        description: `Loaded "${template.name}" with ${items.length} items. You can now modify the details and select a stage.`,
        duration: 4000,
      });
    } catch (error) {
      console.error('Error loading template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Add new template
  const handleAddTemplate = async () => {
    try {
      if (!newTemplate.name || !newTemplate.stage_id) {
        toast({
          title: "Error",
          description: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }

      const tenantId = await getCurrentTenantId();

      let data, error;

      if (templateMode && templateId) {
        // Template mode: Insert into checklist_templates_library
        const selectedStage = stages.find(s =>
          String(s.id) === String(newTemplate.stage_id) ||
          s.name === newTemplate.stage_id
        );

        const libraryData = {
          name: newTemplate.name,
          description: newTemplate.description,
          category: 'general',
          stage_name: selectedStage?.name || newTemplate.stage_id,
          pipeline_template_id: templateId, // Link to pipeline template
          scope: 'tenant',
          configuration: {
            items: [],
            default_settings: {
              auto_notify_pm_at_percent: 80,
              auto_move_stage_when_complete: false,
              estimated_completion_time_hours: 2
            }
          },
          usage_stats: {
            times_applied: 0,
            avg_completion_rate: 0,
            last_used: null
          },
          is_active: true,
          created_by: user?.id,
          owner_tenant_id: tenantId,
          is_legacy: false
        };

        const result = await supabase
          .from('checklist_templates_library')
          .insert([libraryData])
          .select();

        data = result.data;
        error = result.error;
      } else {
        // Pipeline mode: Insert into checklist_templates
        const templateData: any = {
          name: newTemplate.name,
          description: newTemplate.description,
          stage_id: parseInt(newTemplate.stage_id),
          checklist_type: newTemplate.checklist_type
        };

        // Add pipeline_id for commercial checklists
        if (newTemplate.checklist_type === 'commercial' && newTemplate.pipeline_id) {
          templateData.pipeline_id = parseInt(newTemplate.pipeline_id);
        }

        // If pipelineId is provided as a prop, use it (for pipeline-specific creation)
        if (pipelineId && !templateData.pipeline_id) {
          templateData.pipeline_id = pipelineId;
        }

        const result = await supabase
          .from('checklist_templates')
          .insert([{
            ...templateData,
            tenant_id: tenantId
          }])
          .select();

        data = result.data;
        error = result.error;
      }

      if (error) {
        // In pipeline mode, if the table doesn't exist, create it
        if (!templateMode && error.code === '42P01') { // PostgreSQL code for "relation does not exist"

          // Create the table
          const createTableQuery = `
            CREATE TABLE IF NOT EXISTS public.checklist_templates (
              id SERIAL PRIMARY KEY,
              name TEXT NOT NULL,
              description TEXT,
              stage_id INTEGER NOT NULL,
              checklist_type TEXT DEFAULT 'residential',
              created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
              updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
            );
          `;

          const { error: createError } = await supabase.rpc('exec', { query: createTableQuery });

          if (createError) throw createError;

          // Try inserting again - only in pipeline mode
          const templateData: any = {
            name: newTemplate.name,
            description: newTemplate.description,
            stage_id: parseInt(newTemplate.stage_id),
            checklist_type: newTemplate.checklist_type
          };

          if (newTemplate.checklist_type === 'commercial' && newTemplate.pipeline_id) {
            templateData.pipeline_id = parseInt(newTemplate.pipeline_id);
          }

          if (pipelineId && !templateData.pipeline_id) {
            templateData.pipeline_id = pipelineId;
          }

          const { data: retryData, error: retryError } = await supabase
            .from('checklist_templates')
            .insert([{
              ...templateData,
              tenant_id: tenantId
            }])
            .select();

          if (retryError) throw retryError;

          if (retryData) {
            setTemplates([...templates, retryData[0]]);
          }
        } else {
          throw error;
        }
      } else if (data) {
        const newTemplateData = data[0];
        setTemplates([...templates, newTemplateData]);

        // If we loaded a template with items, create those items for the new template
        const loadedItems = checklistItems['new_template'];
        if (loadedItems && loadedItems.length > 0) {
          try {
            // Create checklist items for the new template
            const itemsToInsert = loadedItems.map((item, index) => ({
              template_id: newTemplateData.id,
              item_name: item.name || `Item ${index + 1}`,
              item_type: item.item_type || 'CHECKBOX',
              is_required: item.is_required || false,
              item_order: index,
              item_category: 'project',
              // EXP-1.3 · point back. Record which library item this came
              // from, so the field resolves that item's example photos rather
              // than owning a copy. The both-or-neither rule and its reasoning
              // live in src/lib/libraryItemLink.ts, with its own tests.
              ...libraryItemLink(loadedLibraryTemplateId, item.id),
              // EXP-2.1 · carry the library item's switch across too.
              // ⚠️ Visibility becomes LOCAL from here: the pipeline's own
              // column governs whether the chip shows, while the content still
              // points back. Gating on the library's flag instead would let an
              // office switching guidance off on a template silently blank
              // every pipeline built from it.
              examples_enabled: item.examples_enabled === true,
              // Must be the caller's tenant. This was hardcoded to the Solerro
              // tenant, and because the checklist_items INSERT policy is
              // WITH CHECK (true) the write succeeded silently — so an admin in
              // any other tenant created a template whose items were owned by
              // Solerro.
              tenant_id: tenantId
            }));

            const { error: itemsError } = await supabase
              .from('checklist_items')
              .insert(itemsToInsert);

            if (itemsError) {
              console.error('Error creating checklist items:', itemsError);
              toast({
                title: "Warning",
                description: "Template created but some items failed to load",
                variant: "destructive",
              });
            } else {
              toast({
                title: "Success",
                description: `Checklist template created with ${loadedItems.length} items!`
              });
            }

            // Clear the loaded items
            setChecklistItems(prev => {
              const { 'new_template': _, ...rest } = prev;
              return rest;
            });
            // EXP-1.3: and the library id with them. Leaving it set would link
            // the NEXT template's items back to this one.
            setLoadedLibraryTemplateId(null);
          } catch (error) {
            console.error('Error creating checklist items:', error);
            toast({
              title: "Warning",
              description: "Template created but items failed to load",
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Success",
            description: "Checklist template added successfully!"
          });
        }
      }

      setNewTemplate({
        name: "",
        description: "",
        stage_id: "",
        checklist_type: pipelineId ? pipelineType || "residential" : activeChecklistTab,
        pipeline_id: pipelineId ? pipelineId.toString() : ""
      });
      
      setIsTemplateDialogOpen(false);
      setShowInlineTemplateForm(false);
      
      // Refresh templates to ensure the new template appears in the filtered list
      await fetchTemplates();
      
      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Error adding checklist template:', error);
      toast({
        title: "Error",
        description: `Failed to add checklist template: ${describeSupabaseError(error)}`,
        variant: "destructive",
      });
    }
  };

  // Update existing template
  const handleUpdateTemplate = async () => {
    try {
      if (!selectedTemplate || !newTemplate.name || !newTemplate.stage_id) {
        toast({
          title: "Error",
          description: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }

      let data, error;

      if (templateMode && templateId) {
        // Template mode: Update in checklist_templates_library
        const selectedStage = stages.find(s =>
          String(s.id) === String(newTemplate.stage_id) ||
          s.name === newTemplate.stage_id
        );

        const updateData = {
          name: newTemplate.name,
          description: newTemplate.description,
          stage_name: selectedStage?.name || newTemplate.stage_id,
          updated_at: new Date().toISOString()
        };

        const result = await supabase
          .from('checklist_templates_library')
          .update(updateData)
          .eq('id', selectedTemplate.id)
          .select();

        data = result.data;
        error = result.error;
      } else {
        // Pipeline mode: Update in checklist_templates
        const updateData: any = {
          name: newTemplate.name,
          description: newTemplate.description,
          stage_id: parseInt(newTemplate.stage_id),
          checklist_type: newTemplate.checklist_type,
          updated_at: new Date().toISOString()
        };

        // Add pipeline_id for commercial checklists
        if (newTemplate.checklist_type === 'commercial' && newTemplate.pipeline_id) {
          updateData.pipeline_id = parseInt(newTemplate.pipeline_id);
        }

        const result = await supabase
          .from('checklist_templates')
          .update(updateData)
          .eq('id', selectedTemplate.id)
          .select();

        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      // A zero-row UPDATE is reported as success by PostgREST, so an RLS USING
      // mismatch (e.g. editing a global template, which the tenant does not own)
      // arrives here as data === []. That is truthy, so data[0] used to write
      // `undefined` into the template list while toasting success.
      if (!data || data.length === 0) {
        throw new Error(
          'No template was updated. This template is not owned by your tenant, so it cannot be edited here.'
        );
      }

      setTemplates(templates.map(template =>
        template.id === selectedTemplate.id ? data[0] : template
      ));

      setNewTemplate({
        name: "",
        description: "",
        stage_id: "",
        checklist_type: pipelineId ? pipelineType || "residential" : activeChecklistTab,
        pipeline_id: pipelineId ? pipelineId.toString() : ""
      });
      
      setSelectedTemplate(null);
      setIsTemplateDialogOpen(false);
      setShowInlineTemplateForm(false);
      
      toast({
        title: "Success",
        description: "Checklist template updated successfully!"
      });
      
      // Refresh templates to ensure the updated template appears correctly
      await fetchTemplates();
      
      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Error updating checklist template:', error);
      toast({
        title: "Error",
        description: `Failed to update checklist template: ${describeSupabaseError(error)}`,
        variant: "destructive",
      });
    }
  };

  // Delete template
  const confirmDeleteTemplate = async () => {
    try {
      if (!deletingTemplate) return;

      if (templateMode) {
        // Template mode: Delete from checklist_templates_library
        // Items are stored in configuration JSONB, so just delete the template
        //
        // Like UPDATE, a DELETE the RLS USING clause rejects removes zero rows
        // and reports success — the row would vanish from the list and come back
        // on the next refresh. Count what was actually deleted.
        const { data: deleted, error } = await supabase
          .from('checklist_templates_library')
          .delete()
          .eq('id', deletingTemplate.id)
          .select('id');

        if (error) throw error;
        if (!deleted || deleted.length === 0) {
          throw new Error(
            'No template was deleted. This template is not owned by your tenant, so it cannot be deleted here.'
          );
        }
      } else {
        // Pipeline mode: Delete from checklist_templates
        // First delete all items associated with this template
        const { error: itemsError } = await supabase
          .from('checklist_items')
          .delete()
          .eq('template_id', deletingTemplate.id);

        if (itemsError) throw itemsError;

        // Then delete the template
        const { data: deleted, error } = await supabase
          .from('checklist_templates')
          .delete()
          .eq('id', deletingTemplate.id)
          .select('id');

        if (error) throw error;
        if (!deleted || deleted.length === 0) {
          throw new Error(
            'No template was deleted. It may belong to another tenant.'
          );
        }
      }

      setTemplates(templates.filter(template => template.id !== deletingTemplate.id));
      setDeletingTemplate(null);
      setShowInlineDeleteConfirmation(false);

      toast({
        title: "Success",
        description: "Checklist template deleted successfully!"
      });

      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Error deleting checklist template:', error);
      toast({
        title: "Error",
        description: `Failed to delete checklist template: ${describeSupabaseError(error)}`,
        variant: "destructive",
      });
    }
  };

  // Handle edit template
  const handleEditTemplate = (template: ChecklistTemplate) => {
    setSelectedTemplate(template);

    // In template mode, templates use stage_name instead of stage_id
    // Find the matching stage to get the ID/name
    let stageIdOrName = '';
    if (templateMode) {
      // In template mode, find stage by name
      const matchingStage = stages.find(s => s.name === template.stage_name);
      stageIdOrName = matchingStage ? String(matchingStage.id || matchingStage.name) : (template.stage_name || '');
    } else {
      // In pipeline mode, use stage_id directly
      stageIdOrName = template.stage_id?.toString() || '';
    }

    setNewTemplate({
      name: template.name,
      description: template.description || "",
      stage_id: stageIdOrName,
      checklist_type: template.checklist_type || activeChecklistTab,
      pipeline_id: template.pipeline_id?.toString() || ""
    });
    setTemplateSelectionMode('create');
    setSelectedTemplateToLoad('');

    // Always show the inline form for editing
    // If we're in the item manager view, close it first
    if (showItemManager) {
      setShowItemManager(false);
    }
    setShowInlineTemplateForm(true);
  };

  // Handle delete template
  const handleDeleteTemplate = (template: ChecklistTemplate) => {
    setDeletingTemplate(template);
    setShowInlineDeleteConfirmation(true);
  };

  // Handle manage items
  const handleManageItems = (template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setShowItemManager(true);
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center">
          <ClipboardList className="mr-2 h-5 w-5 text-primary" />
          <div>
            <CardTitle>Checklists</CardTitle>
            <CardDescription>Manage checklist templates for project stages</CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasOrderChanges && (
            <Button 
              onClick={saveTemplateOrder} 
              disabled={savingOrder}
              variant="outline"
              className="bg-success/5 border-success/20 text-success hover:bg-success/10"
            >
              {savingOrder ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700 mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
          <Button onClick={() => {
            setSelectedTemplate(null);
            setNewTemplate({
              name: "",
              description: "",
              stage_id: "",
              checklist_type: activeChecklistTab,
              pipeline_id: ""
            });
            setTemplateSelectionMode('create');
            setSelectedTemplateToLoad('');
            setShowInlineTemplateForm(true);
          }}>
            Add Template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showItemManager && selectedTemplate ? (
          // Item Manager View
          <ChecklistItemManager
            templateId={templateMode ? 0 : (typeof selectedTemplate.id === 'string' ? parseInt(selectedTemplate.id) : selectedTemplate.id)}
            templateName={selectedTemplate.name}
            templateMode={templateMode}
            templateStringId={templateMode ? String(selectedTemplate.id) : undefined}
            isOpen={true}
            registerExitGuard={registerExitGuard}
            onClose={() => {
              setShowItemManager(false);
              const templateIdToRefresh = selectedTemplate?.id;
              setSelectedTemplate(null);
              // Refresh templates to update item counts and any metadata changes
              fetchTemplates();
              // Also refresh the specific template's items if we have an ID
              if (templateIdToRefresh) {
                fetchTemplateItems(templateIdToRefresh);
              }
            }}
            noPanel={true}
            onBack={() => {
              setShowItemManager(false);
              const templateIdToRefresh = selectedTemplate?.id;
              setSelectedTemplate(null);
              // Refresh templates to update item counts and any metadata changes
              fetchTemplates();
              // Also refresh the specific template's items if we have an ID
              if (templateIdToRefresh) {
                fetchTemplateItems(templateIdToRefresh);
              }
            }}
          />
        ) : showInlineTemplateForm ? (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-foreground">
                {selectedTemplate ? 'Edit Template' : 'Add New Template'}
              </h3>
              <Button
                variant="outline"
                onClick={() => {
                  setShowInlineTemplateForm(false);
                  setSelectedTemplate(null);
                  setNewTemplate({
                    name: "",
                    description: "",
                    stage_id: "",
                    checklist_type: activeChecklistTab,
                    pipeline_id: ""
                  });
                  setTemplateSelectionMode('create');
                  setSelectedTemplateToLoad('');
                }}
              >
                Cancel
              </Button>
            </div>

            <div className="text-sm text-muted-foreground">
              {selectedTemplate ? 'Update template information.' :
               pipelineId ? 'Add a new checklist template for this specific pipeline.' :
               'Add a new checklist template.'}
            </div>

            {!selectedTemplate && (
              <div className="flex gap-2 border-b pb-4">
                <Button
                  variant={templateSelectionMode === 'create' ? 'default' : 'outline'}
                  onClick={() => setTemplateSelectionMode('create')}
                  className="flex-1"
                >
                  Create New
                </Button>
                <Button
                  variant={templateSelectionMode === 'load' ? 'default' : 'outline'}
                  onClick={() => {
                    setTemplateSelectionMode('load');
                    fetchAvailableTemplates();
                  }}
                  className="flex-1"
                >
                  Load from Template
                </Button>
              </div>
            )}

            <div className="grid gap-4 py-4 overflow-y-auto max-h-[50vh]">
              {templateSelectionMode === 'create' ? (
                <>
                  {checklistItems['new_template'] && checklistItems['new_template'].length > 0 && (
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <div className="bg-primary rounded-full p-1 flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-primary">Template Loaded</h4>
                          <div className="text-xs text-muted-foreground mt-1">
                            <p>Template loaded with {checklistItems['new_template'].length} items. You can modify the details below.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="grid gap-2">
                    <FormField label="Stage *" htmlFor="stage_id">
                      <Select
                        value={newTemplate.stage_id}
                        onValueChange={(value) => setNewTemplate({ ...newTemplate, stage_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                        <SelectContent>
                          {stageOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Template Name *" htmlFor="template_name">
                      <Input
                        id="template_name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        placeholder="Enter template name"
                      />
                    </FormField>
                    <FormField label="Description" htmlFor="template_description">
                      <Input
                        id="template_description"
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                        placeholder="Enter template description"
                      />
                    </FormField>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <FormField label="Select Template to Load" htmlFor="template_select">
                    <Select
                      value={selectedTemplateToLoad}
                      onValueChange={setSelectedTemplateToLoad}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select template to load" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTemplates.map(template => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name} ({template.stage_name || 'No Stage'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  {selectedTemplateToLoad && (() => {
                    const template = availableTemplates.find(t => t.id.toString() === selectedTemplateToLoad);
                    return template ? (
                      <div className="bg-muted/50 rounded-md p-4">
                        <h4 className="font-medium text-foreground mb-2">{template.name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{template.description || 'No description available'}</p>
                        <div className="text-xs text-muted-foreground">
                          <p><strong>Stage:</strong> {template.stage_name || 'No stage specified'}</p>
                          <p><strong>Items:</strong> {template.configuration?.items?.length || 0} checklist items</p>
                          <p className="mt-2">You can modify the details after loading.</p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowInlineTemplateForm(false);
                  setSelectedTemplate(null);
                  setNewTemplate({
                    name: "",
                    description: "",
                    stage_id: "",
                    checklist_type: activeChecklistTab,
                    pipeline_id: ""
                  });
                  setTemplateSelectionMode('create');
                  setSelectedTemplateToLoad('');
                }}
              >
                Cancel
              </Button>
              {templateSelectionMode === 'load' && selectedTemplateToLoad ? (
                <Button
                  disabled={isLoadingTemplate}
                  onClick={handleLoadTemplate}
                >
                  {isLoadingTemplate ? "Loading..." : "Load Template"}
                </Button>
              ) : (
                <Button
                  disabled={!newTemplate.name || !newTemplate.stage_id}
                  onClick={selectedTemplate ? handleUpdateTemplate : handleAddTemplate}
                >
                  {selectedTemplate ? "Update Template" : "Add Template"}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <Tabs
            defaultValue="residential"
            value={activeChecklistTab}
            onValueChange={setActiveChecklistTab}
            className="w-full"
          >
            {!pipelineId && !templateMode && (
              <TabsList className="mb-4">
                <TabsTrigger value="residential">Residential Checklists</TabsTrigger>
                <TabsTrigger value="commercial">Commercial Checklists</TabsTrigger>
              </TabsList>
            )}
            
            <TabsContent value="residential">
              {loading ? (
                <div className="flex justify-center py-8">
                  <p>Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No checklist templates found. Create your first template!</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="residential-templates">
                    {(provided: DroppableProvided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-4"
                      >
                        {/* In template mode, show ALL checklists for this template regardless of type.
                            Checklists inherit their type from the parent pipeline template, not from a separate field.
                            In pipeline mode, filter by checklist_type for residential/commercial tabs. */}
                        {templates
                          .filter(template => templateMode || template.checklist_type !== 'commercial')
                          .map((template, index, filteredArray) => (
                            <Draggable
                              key={template.id}
                              draggableId={template.id.toString()}
                              index={index}
                            >
                              {(provided: DraggableProvided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  {showInlineDeleteConfirmation && deletingTemplate?.id === template.id ? (
                                    <div className="border border-orange-300 rounded-md p-4 bg-orange-50">
                                      <div className="flex items-start gap-3 mb-4">
                                        <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-semibold text-orange-800 mb-1">Delete "{template.name}"?</h4>
                                          <ul className="text-xs text-orange-700 space-y-1">
                                            <li>• This action cannot be undone</li>
                                            <li>• All checklist items will be permanently deleted</li>
                                            {templateMode && <li>• This will affect all pipelines using this template</li>}
                                          </ul>
                                        </div>
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setShowInlineDeleteConfirmation(false);
                                            setDeletingTemplate(null);
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={confirmDeleteTemplate}
                                        >
                                          Delete Checklist
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <ChecklistCard
                                      template={template}
                                      index={index}
                                      totalCount={filteredArray.length}
                                      stages={stages}
                                      checklistItems={checklistItems}
                                      loading={loading}
                                      type="residential"
                                      templateMode={templateMode}
                                      onMoveUp={handleMoveUp}
                                      onMoveDown={handleMoveDown}
                                      onManageItems={handleManageItems}
                                      onEdit={handleEditTemplate}
                                      onDelete={handleDeleteTemplate}
                                      dragHandleProps={provided.dragHandleProps}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </TabsContent>
            
            <TabsContent value="commercial">
              {loading ? (
                <div className="flex justify-center py-8">
                  <p>Loading templates...</p>
                </div>
              ) : templateMode ? (
                /* Template mode: Show all templates in simple list (same structure as residential) */
                templates.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No checklist templates found. Create your first template!</p>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="commercial-templates">
                      {(provided: DroppableProvided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-4"
                        >
                          {templates.map((template, index, filteredArray) => (
                            <Draggable
                              key={template.id}
                              draggableId={template.id.toString()}
                              index={index}
                            >
                              {(provided: DraggableProvided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  {showInlineDeleteConfirmation && deletingTemplate?.id === template.id ? (
                                    <div className="border border-orange-300 rounded-md p-4 bg-orange-50">
                                      <div className="flex items-start gap-3 mb-4">
                                        <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-semibold text-orange-800 mb-1">Delete "{template.name}"?</h4>
                                          <ul className="text-xs text-orange-700 space-y-1">
                                            <li>• This action cannot be undone</li>
                                            <li>• All checklist items will be permanently deleted</li>
                                            {templateMode && <li>• This will affect all pipelines using this template</li>}
                                          </ul>
                                        </div>
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setShowInlineDeleteConfirmation(false);
                                            setDeletingTemplate(null);
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={confirmDeleteTemplate}
                                        >
                                          Delete Checklist
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <ChecklistCard
                                      template={template}
                                      index={index}
                                      totalCount={filteredArray.length}
                                      stages={stages}
                                      checklistItems={checklistItems}
                                      loading={loading}
                                      type="commercial"
                                      templateMode={templateMode}
                                      onMoveUp={handleMoveUp}
                                      onMoveDown={handleMoveDown}
                                      onManageItems={handleManageItems}
                                      onEdit={handleEditTemplate}
                                      onDelete={handleDeleteTemplate}
                                      dragHandleProps={provided.dragHandleProps}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )
              ) : templates.filter(template => template.checklist_type === 'commercial').length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No commercial checklist templates found. Create your first template!</p>
                </div>
              ) : (
                /* Pipeline mode: Group by pipeline */
                <div className="space-y-6">
                  {pipelines
                    .filter(pipeline => pipeline.pipeline_type === 'commercial')
                    .map(pipeline => {
                      const pipelineTemplates = templates.filter(template =>
                        template.checklist_type === 'commercial' &&
                        template.pipeline_id === pipeline.id
                      );

                      if (pipelineTemplates.length === 0) return null;

                      return (
                        <div key={pipeline.id} className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold">{pipeline.name}</h3>
                            <span className="text-sm text-muted-foreground">
                              ({pipelineTemplates.length} template{pipelineTemplates.length !== 1 ? 's' : ''})
                            </span>
                          </div>
                          <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId={`commercial-templates-${pipeline.id}`}>
                              {(provided: DroppableProvided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className="space-y-4 pl-4 border-l-2 border-border"
                                >
                                  {pipelineTemplates.map((template, index) => (
                                    <Draggable
                                      key={template.id}
                                      draggableId={template.id.toString()}
                                      index={index}
                                    >
                                      {(provided: DraggableProvided) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                        >
                                          {showInlineDeleteConfirmation && deletingTemplate?.id === template.id ? (
                                            <div className="border border-orange-300 rounded-md p-4 bg-orange-50">
                                              <div className="flex items-start gap-3 mb-4">
                                                <div className="bg-orange-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1">
                                                  <h4 className="text-sm font-semibold text-orange-800 mb-1">Delete "{template.name}"?</h4>
                                                  <ul className="text-xs text-orange-700 space-y-1">
                                                    <li>• This action cannot be undone</li>
                                                    <li>• All checklist items will be permanently deleted</li>
                                                    {templateMode && <li>• This will affect all pipelines using this template</li>}
                                                  </ul>
                                                </div>
                                              </div>
                                              <div className="flex justify-end gap-2">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => {
                                                    setShowInlineDeleteConfirmation(false);
                                                    setDeletingTemplate(null);
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  variant="destructive"
                                                  size="sm"
                                                  onClick={confirmDeleteTemplate}
                                                >
                                                  Delete Checklist
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <ChecklistCard
                                              template={template}
                                              index={index}
                                              totalCount={pipelineTemplates.length}
                                              stages={stages}
                                              checklistItems={checklistItems}
                                              loading={loading}
                                              type="commercial"
                                              templateMode={templateMode}
                                              onMoveUp={handleMoveUp}
                                              onMoveDown={handleMoveDown}
                                              onManageItems={handleManageItems}
                                              onEdit={handleEditTemplate}
                                              onDelete={handleDeleteTemplate}
                                              dragHandleProps={provided.dragHandleProps}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </div>
                      );
                    })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

      </CardContent>
    </Card>
  );
};
